import { type MonthGuidance, useGuidance } from "@/hooks/use-guidance";
import { availabilityPercent, type GuidanceTier, guidanceTier } from "@/lib/guidance-tier";
import { reservationCoversMonth, reservedHours } from "@/lib/resource-reservation";

// Reuse the existing fit-tier CSS vars — do not invent a new color scale (WI-002 AC).
const TIER_CLASS: Record<GuidanceTier, string> = {
  low: "bg-[var(--fit-strong-bg)] text-[var(--fit-strong)]",
  moderate: "bg-[var(--fit-moderate-bg)] text-[var(--fit-moderate)]",
  high: "bg-[var(--fit-at-risk-bg)] text-[var(--fit-at-risk)]",
  none: "bg-muted text-muted-foreground",
};

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Open time reserved for another Account (docs/specs/resource-reservation.md).
const RESERVED_CLASS = "bg-[var(--reserved-bg)] text-[var(--reserved)]";

// Subtle: month label + tier color only. Full detail (hours / capacity /
// availability) stays available on hover via the title tooltip — keeps the
// assignment row low-cognitive-load so the Fit Score can dominate.

function Cell({
  m,
  reservedFor,
}: {
  m: MonthGuidance;
  reservedFor: GuidanceStripReservation | null;
}) {
  const tier = guidanceTier(m.guidanceHours, m.capacityHours);
  const avail = availabilityPercent(m.guidanceHours, m.capacityHours);
  const label = MONTH_ABBR[m.month - 1] ?? "";
  // Reserved for another Account: the open time is not free for THIS Solution. Overload
  // keeps its red — it is the more urgent read.
  const isReserved = reservedFor !== null && reservationCoversMonth(reservedFor, m.monthStart);
  const reservedHrs = isReserved ? reservedHours(m.guidanceHours, m.capacityHours) : null;
  const availability =
    isReserved && reservedFor !== null
      ? `${reservedHrs === null ? "open time" : `${Math.round(reservedHrs)}h`} reserved for ${reservedFor.accountName}`
      : avail === null
        ? "availability unknown"
        : `${avail}% free`;
  return (
    <div
      className={`flex min-w-[26px] flex-1 items-center justify-center rounded px-1 py-1 ${
        isReserved && tier !== "high" ? RESERVED_CLASS : TIER_CLASS[tier]
      } ${m.isCurrent ? "ring-1 ring-brand-500" : ""}`}
      title={`${label} ${m.year}${m.isCurrent ? " (current month)" : ""} · guidance ${m.guidanceHours ?? "—"}h / capacity ${m.capacityHours ?? "—"}h · ${availability}`}
    >
      <span className="text-xs font-semibold uppercase leading-none tracking-wide opacity-80">
        {label}
      </span>
    </div>
  );
}

/** A reservation for ANOTHER Account than the one being staffed (docs/specs/resource-reservation.md). */
export interface GuidanceStripReservation {
  accountName: string;
  /** Last day of the reservation, `YYYY-MM-DD`, inclusive. null = open-ended. */
  until: string | null;
}

export function GuidanceStrip({
  resourceId,
  reservedFor = null,
}: {
  resourceId: string;
  /**
   * Pass only when the Resource is reserved for a DIFFERENT Account than the Solution being
   * staffed — a reservation for the same Account leaves its open time available to that work.
   */
  reservedFor?: GuidanceStripReservation | null;
}) {
  const { byResource, isLoading } = useGuidance();
  if (isLoading) return null;
  const months = byResource.get(resourceId);
  if (!months || months.length === 0) return null;
  return (
    <div className="mt-1 flex gap-0.5">
      {months.slice(0, 4).map((m) => (
        <Cell key={m.monthStart} m={m} reservedFor={reservedFor} />
      ))}
    </div>
  );
}
